import { useState } from "react";
import {
  BasketballIcon,
  ChatCircleDotsIcon,
  LightningIcon,
  BroadcastIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  XIcon,
} from "@phosphor-icons/react";

const STEPS = [
  {
    intro: true,
    title: "Hi, I'm Gamly!",
    body: "Let me show you around. I'm your AI sports companion — I explain every score, storyline, and big moment in real time.",
  },
  {
    icon: BasketballIcon,
    accent: "#0A84FF",
    title: "Real Live Games",
    body: "Today's Games shows real live scores across MLB, the NBA Finals, and the FIFA World Cup — with AI win-probability and storylines.",
  },
  {
    icon: ChatCircleDotsIcon,
    accent: "#0A84FF",
    title: "Watch With Gamly",
    body: "Get a live companion feed and ask me anything mid-game — in my Broadcaster, Analyst, Coach, or Trash-Talker voice.",
  },
  {
    icon: LightningIcon,
    accent: "#FF453A",
    title: "Hot Takes & Fan XP",
    body: "Vote on daily debates, predict the next play, and earn Fan XP to climb from Rookie Fan all the way to Legend.",
  },
  {
    icon: BroadcastIcon,
    accent: "#FDB927",
    title: "Press Box Mode",
    body: "Pros get a Broadcast Control Room: talking points, Game Day shot charts & pitch views, interview packs, and on-air scripts.",
  },
];

export default function WelcomeTour({ onClose }) {
  const [step, setStep] = useState(0);
  const s = STEPS[step];
  const last = step === STEPS.length - 1;
  const Icon = s.icon;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
      data-testid="welcome-tour"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-gradient-to-b from-[#141925] to-[#0b0f17] border border-white/12 shadow-2xl overflow-hidden">
        {/* glow */}
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-64 h-32 rounded-full bg-[#0A84FF]/25 blur-3xl pointer-events-none" />

        <button
          onClick={onClose}
          data-testid="tour-skip"
          className="absolute top-3 right-3 z-10 text-white/40 hover:text-white text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
        >
          Skip <XIcon size={13} />
        </button>

        <div className="relative px-7 pt-9 pb-6 text-center">
          {/* Gamly icon / step icon */}
          <div className="flex justify-center mb-5">
            {s.intro ? (
              <div className="px-5 py-4 rounded-xl bg-black/40 border border-white/10 inline-flex items-center gap-2">
                <img src="/gamly-logo.png" alt="Gamly AI" className="h-12 w-auto" data-testid="tour-gamly-icon" />
                <span className="text-[9px] font-black uppercase tracking-[0.12em] text-[#0A84FF] bg-[#0A84FF]/12 border border-[#0A84FF]/30 rounded px-1 py-0.5 leading-none">Beta</span>
              </div>
            ) : (
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${s.accent}1f`, border: `1px solid ${s.accent}55` }}
              >
                {Icon && <Icon weight="fill" size={30} style={{ color: s.accent }} />}
              </div>
            )}
          </div>

          <h2 className="font-display font-bold text-3xl uppercase tracking-tighter mb-2" data-testid="tour-title">
            {s.title}
          </h2>
          <p className="text-white/65 text-sm leading-relaxed max-w-xs mx-auto" data-testid="tour-body">
            {s.body}
          </p>

          {/* progress dots */}
          <div className="flex items-center justify-center gap-1.5 mt-6">
            {STEPS.map((st, i) => (
              <button
                key={st.title}
                onClick={() => setStep(i)}
                data-testid={`tour-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#0A84FF]" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* footer controls */}
        <div className="px-7 py-4 border-t border-white/8 flex items-center justify-between bg-black/30">
          <button
            onClick={() => setStep((v) => Math.max(0, v - 1))}
            disabled={step === 0}
            data-testid="tour-back"
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-white/55 hover:text-white disabled:opacity-0 transition-opacity"
          >
            <ArrowLeftIcon size={13} /> Back
          </button>

          {last ? (
            <button
              onClick={onClose}
              data-testid="tour-start"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FDB927] hover:bg-yellow-400 text-black text-xs font-black uppercase tracking-wider transition-colors"
            >
              Start Exploring <ArrowRightIcon weight="bold" size={14} />
            </button>
          ) : (
            <button
              onClick={() => setStep((v) => Math.min(STEPS.length - 1, v + 1))}
              data-testid="tour-next"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0A84FF] hover:bg-blue-600 text-white text-xs font-black uppercase tracking-wider transition-colors"
            >
              Next <ArrowRightIcon weight="bold" size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
